import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import { useBoardState, useBoardActions } from '../../../context/BoardContext';
import { useAgendaColumnInfiniteData } from '../../../hooks/queries/board/useAgendaColumnInfiniteData';
import { Controller, useForm } from 'react-hook-form';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import {
  isTaskOverdue,
  handleDateTimeOffset,
  getDateBoundaryInTimezone,
  convertDateFromApiToUserTimezone,
  convertToUserTimezoneDateOnly,
} from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import constants from 'constants/index';
import { TASK_STAGES } from '../../../utils/constants';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import ImageComponent from 'shared/ImageComponent';
import { GroupUserTooltip } from 'components/UI/GroupUserTooltip/GroupUserTooltip';
import { generateFullName } from 'utils/generateFullNameUtils';
import { AvatarGroup } from 'primereact/avatargroup';
import { Avatar } from 'primereact/avatar';
import { Tooltip } from 'primereact/tooltip';
import { Checkbox } from 'primereact/checkbox';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import { TieredMenu } from 'primereact/tieredmenu';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { formatStageName, getTenantDetails, formatTaskContactList } from 'utils/utils';
import PriorityDropdown from 'components/UI/PriorityDropdown/PriorityDropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { ManageColumn } from '../Stage';
import { useTaskActions } from 'modules/TasksV2/context';
import { RenameColumnDialog } from '../Column';
import useIsMobileView from 'hooks/useIsMobileView';

import { getClientUserList } from 'services/case/caseServices';
import { assignToItemTemplate } from '../../Common/PriorityDropdownTemplate';
import { useBoardSection } from '../../../context/BoardSectionContext';
import './TableContent.scss';

/**
 * TableContent Component - Displays tasks in a table format for Agenda View
 * Fetches data from the Agenda View API via useAgendaColumnInfiniteData hook
 * Uses the cache populated by useInitialAgendaLoad in AgendaView component
 *
 * @param {Object} stage - The task stage/column object
 * @param {number} index - The stage index
 * @param {Object} filters - Filter parameters (searchKeyword, practiceArea, assignedTo, etc.)
 * @param {Array} taskStages - Array of all task stages
 */

// Store subtask toggle state outside component to persist across rerenders (similar to TaskCard)
const subtaskToggleState = new Map();

const TableContent = ({ stage, index, filters = {}, taskStages, currentView = 'agenda' }) => {
  const isMobileView = useIsMobileView();
  // Pagination state for table display (5 items per page by default)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // Dynamic page size

  // Dialog and form states
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDueDateDialog, setShowDueDateDialog] = useState(false);
  const [showPriorityDialog, setShowPriorityDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);

  // Form values
  const [editDueDate, setEditDueDate] = useState(null);
  const [editPriority, setEditPriority] = useState('');
  const [targetStage, setTargetStage] = useState('');
  const [showRenameStageDialog, setShowRenameStageDialog] = useState(false);
  const [showManageColumn, setShowManageColumn] = useState(false);
  const [clientData, setClientData] = useState([]);
  const [assigneeData, setAssigneeData] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const { isFromCase, isFromLead } = useBoardSection();
  const {
    control,
    watch,
    trigger,
    reset,
    clearErrors,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      assignees: [],
      priority: '',
      dueDate: null,
    },
  });

  // Use a Map to store menu refs for each task (each row needs its own menu ref)
  const menuRefs = useRef(new Map());
  const stageMenuRef = useRef(null);
  const fetchingPageRef = useRef(null); // Track which page is currently being fetched

  const assigneesValue = watch('assignees');

  const paginationDropdownOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
  ];

  // Helper function to filter out invalid assignee IDs that don't exist in current options
  const filterValidAssigneeIds = useCallback(
    (valueIds) => {
      if (!Array?.isArray(valueIds) || !Array?.isArray(clientData) || clientData?.length === 0) {
        return [];
      }

      // Create a Set of all valid option IDs from clientData
      const validIds = new Set();
      clientData?.forEach((group) => {
        group?.items?.forEach((item) => {
          // Use the same logic as optionValue: cognito_username || assignee_id || group_id
          const optionId = item?.cognito_username || item?.assignee_id || item?.group_id;
          if (optionId) {
            validIds?.add(optionId);
          }
        });
      });

      // Filter to only include IDs that exist in validIds
      return valueIds?.filter((id) => validIds?.has(id));
    },
    [clientData]
  );

  // Helper function to get or create a menu ref for a specific task
  const getMenuRef = useCallback((taskId) => {
    if (!menuRefs?.current?.has(taskId)) {
      menuRefs?.current?.set(taskId, React.createRef());
    }
    return menuRefs?.current?.get(taskId);
  }, []);

  const to_do_stage_id = useMemo(() => {
    return taskStages?.find((stage) => stage?.task_stage_name === 'to_do')?.task_stage_id;
  }, []);

  // Use Agenda View specific infinite data hook
  // Fetches data from the Agenda View API endpoint
  // Respects the cache populated by useInitialAgendaLoad
  // Pass pageSize as limit to fetch the correct number of items per page
  const {
    items: allItems,
    isLoading,
    totalCount,
    pages,
    fetchNextPage,
    fetchPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAgendaColumnInfiniteData(stage?.task_stage_id, filters, 'POST', currentView, pageSize, to_do_stage_id);

  const userContext = useUserDetailsContext();
  const { isRefetchingData } = useBoardState();
  const { updateSubtask, moveCard, updateCard, renameColumn, deleteColumn, duplicateColumn } = useBoardActions();
  const { addToast } = useToast();
  const { openAddTask } = useTaskActions();
  const queryClient = useQueryClient();
  // State to track which tasks have expanded subtasks
  const [expandedSubtasks, setExpandedSubtasks] = useState(new Set());
  const location = useLocation();
  const [tenantId] = getTenantDetails();

  // Helper function to build task link with preserved query params
  const buildTaskLink = useCallback(
    (taskId) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('id', taskId);
      return `/tasks?${searchParams.toString()}`;
    },
    [location.search]
  );

  // Helper function to refetch only the current page instead of invalidating all pages
  const refetchCurrentPage = useCallback(
    async (targetStageId = null) => {
      try {
        // Always refetch the current component's stage (not the targetStageId parameter)
        // The targetStageId is only used for logging/invalidation of other stages
        const currentStageId = stage?.task_stage_id;

        if (!currentStageId) {
          return;
        }

        // Only refetch if we have fetchPage and currentPage available
        if (fetchPage && currentPage) {
          // Pass forceRefetch=true to bypass cache and always make API call
          await fetchPage(currentPage, true);
        } else {
          // Fallback: invalidate the current stage (with limit in query key)
          queryClient.invalidateQueries({
            queryKey: ['agendaTasks', 'column', currentStageId, filters, pageSize],
            refetchType: 'active',
          });
        }

        // If targetStageId is different from current stage, refetch it if it's being viewed
        if (targetStageId && targetStageId !== currentStageId) {
          // Use refetchQueries with type: 'active' to refetch if the target stage is currently being viewed
          // This will cause the target stage's TableContent component to refetch its current page
          // Note: We use a partial match without limit since we don't know the target's page size
          await queryClient.refetchQueries({
            queryKey: ['agendaTasks', 'column', targetStageId, filters],
            type: 'active', // Only refetch if query is active (being viewed)
          });
        }
      } catch (error) {
        // Fallback: if fetchPage fails, invalidate only the current page's data
        const currentStageId = stage?.task_stage_id;
        if (currentStageId) {
          queryClient.invalidateQueries({
            queryKey: ['agendaTasks', 'column', currentStageId, filters, pageSize],
            refetchType: 'active', // Only refetch active queries
          });
        }
      }
    },
    [fetchPage, currentPage, filters, queryClient, stage?.task_stage_id, pageSize]
  );

  // Calculate paginated items for current page (page-based pagination)
  // Get items directly from the specific page, not by slicing flattened array
  // This ensures correct data when jumping to pages (e.g., page 1 to page 4 without fetching pages 2-3)
  const displayItems = useMemo(() => {
    if (!pages || pages?.length === 0) return [];

    // Find the specific page data for currentPage
    const currentPageData = pages?.find((page) => page?.page === currentPage);

    if (currentPageData && currentPageData?.items && Array?.isArray(currentPageData?.items)) {
      return currentPageData?.items;
    }

    // Fallback: if page not found, return empty array
    // The useEffect will trigger a fetch for this page
    return [];
  }, [pages, currentPage]);

  const doneStage = useMemo(() => {
    return taskStages?.find((item) => item?.task_stage_name?.toLowerCase() === TASK_STAGES.DONE);
  }, [taskStages]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setPageSize(5); // Reset to default page size
  }, [filters]);

  // Check if we need to load data for the current page
  // Use direct page fetch when jumping pages, sequential fetch only for next page
  useEffect(() => {
    if (!fetchPage || isLoading) return;

    // Check if the specific page exists using pages from hook (reacts to cache updates)
    const pageExists = pages?.some((page) => page?.page === currentPage);

    if (!pageExists && fetchingPageRef.current !== currentPage) {
      // Check if we're requesting the next sequential page
      const maxPageInCache = pages?.length > 0 ? Math.max(...pages?.map((p) => p?.page)) : 0;
      const isNextPage = currentPage === maxPageInCache + 1;

      if (isNextPage && hasNextPage && !isFetchingNextPage) {
        // Fetch next page sequentially (for infinite scroll)
        fetchNextPage();
      } else {
        // Jumping to a page that doesn't exist - fetch directly
        fetchingPageRef.current = currentPage;
        fetchPage(currentPage)
          .then(() => {
            fetchingPageRef.current = null;
          })
          .catch((error) => {
            console.error('Failed to fetch page:', error);
            fetchingPageRef.current = null;
          });
      }
    }
  }, [currentPage, pages, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage, fetchPage]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  }, []);

  // Stage menu handlers
  const confirmRenameStage = useCallback(
    async (newName) => {
      if (newName && newName?.trim() !== stage?.task_stage_name) {
        const result = await renameColumn({
          sk: stage?.sk,
          pk: stage?.pk,
          newName: newName?.trim(),
          task_stage_id: stage?.task_stage_id,
        });
        if (!result?.success) {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant?.message?.TASK_UPDATED_FAILED);
        }
      }
    },
    [stage?.task_stage_id, stage?.task_stage_name, stage?.sk, stage?.pk, renameColumn, addToast]
  );

  const handleManageColumn = useCallback(() => {
    setShowManageColumn(true);
  }, []);

  const handleDuplicateStage = useCallback(async () => {
    if (!stage) return;

    const result = await duplicateColumn(stage);

    if (result?.success) {
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TASK_STAGE_CREATED);
    } else if (result?.locked) {
      addToast(
        false,
        toastConstant.toasterType.WARNING,
        toastConstant.api.WARNING,
        'This stage is currently being duplicated. Please wait.'
      );
    } else {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, result?.error?.message || 'Failed to duplicate stage');
    }
  }, [stage, duplicateColumn, addToast]);

  const handleDeleteStage = useCallback(() => {
    const itemCount = allItems?.length || 0;

    confirmDialog({
      message:
        itemCount > 0
          ? `This stage contains tasks. Deleting the stage will also delete the associated tasks. Are you sure you want to proceed with the deletion?`
          : `Are you sure you want to delete "${formatStageName(stage?.task_stage_name)}" stage?`,
      header: 'Confirm Delete Stage',
      icon: 'pi pi-info-circle',
      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',
      acceptClassName: 'p-button-danger',
      style: { width: '450px' },
      rejectClassName: 'outline',
      accept: async () => {
        const result = await deleteColumn(stage?.task_stage_id, stage?.pk);
        if (!result?.success) {
          console.error('Failed to delete column');
        }
      },
      reject: () => {},
    });
  }, [stage?.task_stage_id, stage?.task_stage_name, stage?.pk, allItems, deleteColumn]);

  // Helper function to find task from source data by task_id
  const findTaskById = useCallback(
    (taskId) => {
      const foundTask = allItems?.find((item) => item?.task_id === taskId);
      if (!foundTask) {
        console.error('Task not found in data source:', taskId);
        return null;
      }
      return { ...foundTask };
    },
    [allItems]
  );

  // Menu action handlers
  const handleEditTask = useCallback(
    (taskId) => {
      // Navigate to task details page
      window.location.href = `#${location?.pathname}?id=${taskId}`;
    },
    [location?.pathname]
  );

  const handleUpdateDueDate = useCallback(
    (taskId) => {
      const taskData = findTaskById(taskId);
      if (!taskData) return;

      setSelectedTask(taskData);
      // Convert date from API to user timezone for Calendar component
      const timezone = userContext?.userDetails?.timezone;
      const convertedDate = taskData?.due_date ? convertToUserTimezoneDateOnly(taskData?.due_date, timezone) : null;
      setEditDueDate(convertedDate);
      setValue('dueDate', convertedDate);
      clearErrors('dueDate');
      setShowDueDateDialog(true);
    },
    [findTaskById, userContext?.userDetails?.timezone, setValue, clearErrors]
  );

  const handleChangePriority = useCallback(
    (taskId) => {
      const taskData = findTaskById(taskId);
      if (!taskData) return;

      setSelectedTask(taskData);
      const priorityValue = taskData?.priority || '';
      setEditPriority(priorityValue);
      setValue('priority', priorityValue);
      clearErrors('priority');
      setShowPriorityDialog(true);
    },
    [findTaskById, setValue, clearErrors]
  );

  const handleReassignTask = useCallback(
    (taskId) => {
      const taskData = findTaskById(taskId);
      if (!taskData) return;

      setSelectedTask(taskData);
      const assignees = [];
      const groupIds = [];
      if (taskData?.assign_to?.length > 0) {
        assignees.push(...(taskData?.assign_to?.map((assignee) => assignee?.cognito_username || assignee?.assignee_id) || []));
      }
      if (taskData?.groups?.length > 0) {
        groupIds.push(...(taskData?.groups?.map((group) => group?.group_id) || []));
      }
      const initialAssignees = [...assignees, ...groupIds];
      setValue('assignees', initialAssignees);
      clearErrors('assignees');
      setAssigneeData([...taskData?.assign_to]);
      setSelectedGroups([...taskData?.groups]);
      setShowReassignDialog(true);
    },
    [findTaskById, setValue, clearErrors]
  );

  const handleMoveToStage = useCallback(
    async (taskId, targetStageId = null) => {
      const taskData = findTaskById(taskId);
      if (!taskData) return;

      setSelectedTask(taskData);
      setTargetStage(targetStageId || '');
      if (!targetStageId) {
        setShowMoveDialog(true);
      } else {
        // Direct move
        try {
          await moveCard({
            sourceCardId: taskData?.task_id,
            sourceColumnId: stage?.task_stage_id,
            targetColumnId: targetStageId,
          });
        } catch (error) {
          console.error('Failed to move task:', error);
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant?.message?.TASK_UPDATED_FAILED);
        } finally {
          // Refetch only the current page of source stage instead of invalidating all columns
          await refetchCurrentPage(stage?.task_stage_id);

          // For target stage, refetch if it's being viewed
          if (targetStageId && targetStageId !== stage?.task_stage_id) {
            // Use refetchQueries with type: 'active' to refetch if the target stage is currently being viewed
            // This will cause the target stage's TableContent component to refetch its current page
            await queryClient.refetchQueries({
              queryKey: ['agendaTasks', 'column', targetStageId, filters],
              type: 'active', // Only refetch if query is active (being viewed)
            });
          }
        }
      }
    },
    [findTaskById, stage?.task_stage_id, moveCard]
  );

  const handleSaveDueDate = useCallback(async () => {
    // Strict validation
    if (!selectedTask || !selectedTask?.task_id) {
      console.error('ERROR: No task selected for due date update');
      return;
    }

    // Trigger form validation for dueDate field
    const isDueDateValid = await trigger('dueDate');
    if (!isDueDateValid) {
      console.warn('Due date validation failed');
      return;
    }

    // Get the validated value from form
    const validatedDueDate = watch('dueDate');
    if (!validatedDueDate) {
      console.warn('Missing due date');
      // Trigger validation to show error
      await trigger('dueDate');
      return;
    }

    try {
      // Extract values explicitly to avoid state mutations
      const taskId = selectedTask?.task_id;
      const stageId = selectedTask?.task_stage || stage?.task_stage_id;
      const timezone = userContext?.userDetails?.timezone;

      // Convert date to timezone-aware format with end of day boundary (similar to TimeEntries.js)
      const formattedDate = getDateBoundaryInTimezone(validatedDueDate, timezone, 'end');

      // Ensure we have valid IDs before making API calls
      if (!taskId || !stageId) {
        console.error('Missing required IDs - Task:', taskId, 'Stage:', stageId);
        return;
      }

      // Update the task in the backend via updateCard API
      await updateCard(taskId, stageId, {
        due_date: formattedDate,
        sk: `task#${taskId}`,
        pk: `${tenantId}#${taskId}`,
        task_title: selectedTask?.task_title || selectedTask?.task_name,
        priority: selectedTask?.priority,
        assign_to: selectedTask?.assign_to,
        task_stage: stageId,
      });

      // Also update via moveCard to ensure Kanban view is updated
      await moveCard({
        sourceCardId: taskId,
        sourceColumnId: stageId,
        targetColumnId: stageId,
        taskUpdates: { due_date: formattedDate },
      });

      // Reset all dialog state
      setShowDueDateDialog(false);
      setSelectedTask(null);
      setEditDueDate(null);
      reset({ dueDate: null });
      clearErrors('dueDate');

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant?.message?.TASK_UPDATED_SUCCESSFULLY);

      // Refetch only the current page instead of invalidating all pages
      await refetchCurrentPage(stageId);
    } catch (error) {
      console.error('Failed to update due date:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant?.message?.TASK_UPDATED_FAILED);
    }
  }, [
    selectedTask,
    stage?.task_stage_id,
    moveCard,
    updateCard,
    tenantId,
    userContext?.userDetails?.timezone,
    refetchCurrentPage,
    addToast,
    trigger,
    watch,
    reset,
    clearErrors,
  ]);

  const handleSavePriority = useCallback(async () => {
    // Strict validation
    if (!selectedTask || !selectedTask?.task_id) {
      console.error('ERROR: No task selected for priority update');
      return;
    }

    // Trigger form validation for priority field
    const isPriorityValid = await trigger('priority');
    if (!isPriorityValid) {
      console.warn('Priority validation failed');
      return;
    }

    // Get the validated value from form
    const validatedPriority = watch('priority');
    if (!validatedPriority) {
      console.warn('Missing priority');
      // Trigger validation to show error
      await trigger('priority');
      return;
    }

    try {
      // Extract values explicitly to avoid state mutations
      const taskId = selectedTask?.task_id;
      const stageId = selectedTask?.task_stage || stage?.task_stage_id;

      // Ensure we have valid IDs before making API calls
      if (!taskId || !stageId) {
        console.error('Missing required IDs - Task:', taskId, 'Stage:', stageId);
        return;
      }

      // Update via updateCard API
      await updateCard(taskId, stageId, {
        priority: validatedPriority,
        sk: `task#${taskId}`,
        pk: `${tenantId}#${taskId}`,
        task_title: selectedTask?.task_title || selectedTask?.task_name,
        task_id: selectedTask?.task_id,
        due_date: selectedTask?.due_date,
        assign_to: selectedTask?.assign_to,
        task_stage: stageId,
        // ...selectedTask,
        // priority: editPriority,
      });

      // Also update via moveCard to ensure Kanban view is updated
      await moveCard({
        sourceCardId: taskId,
        sourceColumnId: stageId,
        targetColumnId: stageId,
        taskUpdates: { priority: validatedPriority },
      });

      // Reset all dialog state
      setShowPriorityDialog(false);
      setSelectedTask(null);
      setEditPriority('');
      reset({ priority: '' });
      clearErrors('priority');

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant?.message?.TASK_UPDATED_SUCCESSFULLY);

      // Refetch only the current page instead of invalidating all pages
      await refetchCurrentPage(stageId);
    } catch (error) {
      console.error('Failed to update priority:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant?.message?.TASK_UPDATED_FAILED);
    }
  }, [
    selectedTask,
    stage?.task_stage_id,
    moveCard,
    updateCard,
    tenantId,
    filters,
    queryClient,
    addToast,
    trigger,
    watch,
    reset,
    clearErrors,
    refetchCurrentPage,
  ]);

  const handleSaveReassign = useCallback(async () => {
    // Strict validation
    if (!selectedTask || !selectedTask?.task_id) {
      console.error('ERROR: No task selected for reassign update');
      return;
    }

    // Get current form value and filter out any invalid IDs before validation
    const currentAssigneesValue = watch('assignees') || [];
    const validAssigneesValue = filterValidAssigneeIds(currentAssigneesValue);

    // Update form value if invalid IDs were filtered out
    if (validAssigneesValue?.length !== currentAssigneesValue?.length) {
      setValue('assignees', validAssigneesValue);
    }

    // Trigger form validation for assignees field
    const isAssigneesValid = await trigger('assignees');
    if (!isAssigneesValid) {
      console.warn('Assignees validation failed');
      return;
    }

    // Additional check for assignees form state values (use filtered valid assignees)
    if (!validAssigneesValue || validAssigneesValue?.length === 0) {
      console.warn('Missing assignees');
      // Trigger validation to show error
      await trigger('assignees');
      return;
    }

    try {
      // Extract values explicitly to avoid state mutations
      const taskId = selectedTask?.task_id;
      const stageId = selectedTask?.task_stage || stage?.task_stage_id;

      // Ensure we have valid IDs before making API calls
      if (!taskId || !stageId) {
        console.error('Missing required IDs - Task:', taskId, 'Stage:', stageId);
        return;
      }

      // Rebuild assigneeData and selectedGroups based on valid IDs only
      const validSelectedAssignees = [];
      const validSelectedGroups = [];
      if (Array?.isArray(validAssigneesValue) && Array?.isArray(clientData)) {
        clientData?.forEach((item) => {
          item?.items?.forEach((assignee) => {
            const optionId = assignee?.cognito_username || assignee?.assignee_id || assignee?.group_id;
            if (validAssigneesValue?.includes(optionId)) {
              if (assignee?.res_type === 'user-group') {
                validSelectedGroups.push(assignee);
              } else {
                validSelectedAssignees.push(assignee);
              }
            }
          });
        });
      }

      // Update via updateCard API
      await updateCard(taskId, stageId, {
        assign_to: validSelectedAssignees,
        groups: validSelectedGroups,
        sk: `task#${taskId}`,
        pk: `${tenantId}#${taskId}`,
        task_title: selectedTask?.task_title || selectedTask?.task_name,
        due_date: selectedTask?.due_date,
        priority: selectedTask?.priority,
        task_stage: stageId,
      });

      // Also update via moveCard to ensure Kanban view is updated
      await moveCard({
        sourceCardId: taskId,
        sourceColumnId: stageId,
        targetColumnId: stageId,
        taskUpdates: { assign_to: validSelectedAssignees, groups: validSelectedGroups },
      });

      // Reset all dialog state
      setShowReassignDialog(false);
      setSelectedTask(null);
      setAssigneeData([]);
      setSelectedGroups([]);

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant?.message?.TASK_UPDATED_SUCCESSFULLY);

      // Refetch only the current page instead of invalidating all pages
      await refetchCurrentPage(stageId);
    } catch (error) {
      console.error('Failed to reassign task:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant?.message?.TASK_UPDATED_FAILED);
    }
  }, [
    selectedTask,
    stage?.task_stage_id,
    moveCard,
    updateCard,
    tenantId,
    filters,
    queryClient,
    addToast,
    trigger,
    refetchCurrentPage,
    filterValidAssigneeIds,
    clientData,
    watch,
    setValue,
  ]);

  const handleSaveMove = useCallback(async () => {
    // Strict validation
    if (!selectedTask || !selectedTask?.task_id) {
      console.error('ERROR: No task selected for move');
      return;
    }
    if (!targetStage) {
      console.warn('Missing target stage');
      return;
    }

    try {
      // Extract values explicitly to avoid state mutations
      const taskId = selectedTask?.task_id;
      const sourceColumnId = selectedTask?.task_stage || stage?.task_stage_id;

      // Ensure we have valid IDs before making API calls
      if (!taskId || !sourceColumnId) {
        console.error('Missing required IDs - Task:', taskId, 'Source Stage:', sourceColumnId);
        return;
      }

      await moveCard({
        sourceCardId: taskId,
        sourceColumnId: sourceColumnId,
        targetColumnId: targetStage,
      });

      // Reset all dialog state
      setShowMoveDialog(false);
      setSelectedTask(null);
      setTargetStage('');

      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant?.message?.TASK_UPDATED_SUCCESSFULLY);

      // Refetch only the current page of source stage instead of invalidating all pages
      await refetchCurrentPage(sourceColumnId);

      // For target stage, refetch if it's being viewed (active query)
      if (targetStage && targetStage !== sourceColumnId) {
        // Use refetchQueries with type: 'active' to refetch if the target stage is currently being viewed
        // This will cause the target stage's TableContent component to refetch its current page
        await queryClient.refetchQueries({
          queryKey: ['agendaTasks', 'column', targetStage, filters],
          type: 'active', // Only refetch if query is active (being viewed)
        });
      }
    } catch (error) {
      console.error('Failed to move task:', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant?.message?.TASK_UPDATED_FAILED);
    }
  }, [selectedTask, targetStage, stage?.task_stage_id, moveCard, filters, queryClient, addToast]);

  const OVERFLOW_AVATAR_STYLE = useMemo(
    () => ({
      backgroundColor: '#C2C5C6',
    }),
    []
  );

  const markAsComplete = useCallback(
    async (taskId) => {
      const taskData = findTaskById(taskId);
      if (!taskData) return;

      // Check if already completed
      if (taskData?.task_status === 'completed' || taskData?._task_stage_ === TASK_STAGES.DONE) {
        return;
      }

      try {
        if (!doneStage) {
          return;
        }

        // Check if task has incomplete subtasks
        const hasIncompleteSubtasks = taskData?.sub_task?.some((subTask) => !subTask?.sub_task_status);

        if (hasIncompleteSubtasks) {
          // Continue with completion...
        }
        await moveCard({
          sourceCardId: taskData?.task_id,
          sourceColumnId: stage?.task_stage_id,
          targetColumnId: doneStage?.task_stage_id,
        });

        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant?.message?.TASK_UPDATED_SUCCESSFULLY);

        // Refetch only the current page of source stage instead of invalidating all pages
        await refetchCurrentPage(stage?.task_stage_id);

        // For target stage (done stage), refetch if it's being viewed
        if (doneStage?.task_stage_id && doneStage?.task_stage_id !== stage?.task_stage_id) {
          // Use refetchQueries with type: 'active' to refetch if the target stage is currently being viewed
          // This will cause the target stage's TableContent component to refetch its current page
          await queryClient.refetchQueries({
            queryKey: ['agendaTasks', 'column', doneStage?.task_stage_id, filters],
            type: 'active', // Only refetch if query is active (being viewed)
          });
        }
      } catch (error) {
        console.error('Failed to mark task as complete:', error);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant?.message?.TASK_UPDATED_FAILED);
      }
    },
    [findTaskById, moveCard, doneStage, stage, filters, queryClient, addToast]
  );

  const isOverdue = useCallback(
    (rowData) => {
      return isTaskOverdue(rowData?.due_date, userContext?.userDetails?.timezone);
    },
    [userContext?.userDetails?.timezone]
  );

  const handleSubTaskCheckBox = useCallback(
    async (checked, subTaskIndex, rowData) => {
      try {
        const result = await updateSubtask(rowData?.task_id, subTaskIndex, checked, rowData, stage?.task_stage_id);

        // Refetch only the current page instead of invalidating all pages
        await refetchCurrentPage(stage?.task_stage_id);

        // If the task was moved to a different stage, refetch the target stage
        // updateSubtask returns { success: true, data: { targetStageId, sourceColumnId, ... } }
        if (result?.success && result?.data?.targetStageId) {
          const targetStageId = result?.data?.targetStageId;
          const sourceColumnId = result?.data?.sourceColumnId || stage?.task_stage_id;

          // If target stage is different from source, refetch it if it's being viewed
          if (targetStageId && targetStageId !== sourceColumnId) {
            await queryClient.refetchQueries({
              queryKey: ['agendaTasks', 'column', targetStageId, filters],
              type: 'active', // Only refetch if query is active (being viewed)
            });
          }
        }
      } catch (error) {
        // logger.error('Failed to update subtask:', error);
        console.error('Failed to update subtask:', error);
      }
    },
    [updateSubtask, stage?.task_stage_id, filters, queryClient, refetchCurrentPage]
  );

  // Template for title column - displays task title and associated case/lead with ellipsis
  const titleTemplate = useCallback(
    (rowData) => {
      const taskTitle = rowData?.task_title || rowData?.title || rowData?.task_name || 'Untitled Task';
      const caseOrLeadName = rowData?.lead_name || rowData?.case_name || '';

      return (
        <>
          <React.Fragment>
            <span className="p-column-title text-break">{isFromCase || isFromLead ? 'Title' : 'Title & Case/Lead'}</span>
            <div className="task-title-wrapper d-flex flex-column gap-2" style={{ minWidth: 0 }}>
              <Link
                to={buildTaskLink(rowData?.task_id)}
                className="text-primary-dark redirection-link-no-text-transform text-decoration-none"
                style={{ minWidth: 0 }}
              >
                <div className="ellipsis-text text-bold" title={taskTitle}>
                  {taskTitle}
                </div>
              </Link>
              {!isFromCase && !isFromLead && (
                <>
                  {rowData?.lead_name || rowData?.case_name ? (
                    <Link
                      to={`${rowData?.is_lead ? '/leads' : '/cases'}/${rowData?.case_id}`}
                      className="text-primary-dark redirection-link-no-text-transform text-decoration-none"
                      style={{ minWidth: 0 }}
                    >
                      <div className="ellipsis-text" style={{ fontSize: '0.875rem' }} title={caseOrLeadName || undefined}>
                        {caseOrLeadName}
                      </div>
                    </Link>
                  ) : (
                    <span className="text-muted small">N/A</span>
                  )}
                </>
              )}
            </div>
          </React.Fragment>
        </>
      );
    },
    [buildTaskLink]
  );

  // Template for status badge
  const statusTemplate = useCallback(
    (rowData) => (
      <React.Fragment>
        <span className="p-column-title text-break">Status</span>
        <span className={`status-badge status-${rowData?.task_status?.toLowerCase() || 'medium'}`}>
          {rowData?.task_status === 'overdue' && isOverdue(rowData) && rowData?._task_stage_ !== TASK_STAGES.DONE && (
            <span className="tag m-1 ms-0 overdue-tag">Overdue</span>
          )}
        </span>
      </React.Fragment>
    ),
    [isOverdue]
  );

  // Template for assignees - can display multiple assignees with proper gap
  const assigneesTemplate = useCallback(
    (rowData) => {
      const assignees = [...(rowData?.assign_to || []), ...(rowData?.groups || [])].map((assignee, idx) => ({
        ...assignee,
        _index: idx,
      }));

      // Get remaining assignees (those with index >= 3)
      const remainingAssignees = assignees?.filter((assignee) => assignee?._index >= 3) || [];

      // Generate unique className for overflow avatar tooltip
      const overflowAvatarId = `overflow-avatar-${rowData?.task_id || 'default'}`;
      const overflowTooltipTarget = `.${overflowAvatarId}`;

      // Format tooltip content for remaining assignees
      const remainingAssigneesTooltip =
        remainingAssignees?.length > 0 ? (
          <div>
            {remainingAssignees?.map((assignee, idx) => (
              <div key={idx} style={{ marginBottom: idx < remainingAssignees.length - 1 ? '0.5rem' : '0' }}>
                {assignee?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                {generateFullName(assignee) ||
                  assignee?.full_name ||
                  assignee?.assignee_name ||
                  assignee?.group_name ||
                  GroupUserTooltip(assignee, assignees)}
              </div>
            ))}
          </div>
        ) : null;

      return (
        <React.Fragment>
          <span className="p-column-title text-break">Assignees</span>
          <div className="assignees-container d-flex align-items-center" style={{ gap: '0.5rem' }}>
            <AvatarGroup>
              {assignees?.map((assignee, idx) => {
                if (idx < 3) {
                  return (
                    <ImageComponent
                      key={idx}
                      filePath={
                        assignee?.contact_id || assignee?.firm_user_id
                          ? `${tenantId}/${assignee?.contact_id ? assignee?.contact_id : assignee?.firm_user_id}/profile_image`
                          : null
                      }
                      fileName={assignee?.first_name || assignee?.assignee_name || assignee?.group_name}
                      className={`task-avatar assignee-${
                        assignee?.assignee_id || assignee?.group_id || assignee?.cognito_username
                      } p-avatar-circle`}
                      toolTipTarget={`.assignee-${assignee?.assignee_id || assignee?.group_id || assignee?.cognito_username}`}
                      toolTipContent={
                        <>
                          {assignee?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                          {generateFullName(assignee) ||
                            assignee?.full_name ||
                            assignee?.assignee_name ||
                            assignee?.group_name ||
                            GroupUserTooltip(assignee, assignees)}
                        </>
                      }
                      tooltipPosition="top"
                      showTooltip={true}
                    />
                  );
                }
                return null;
              })}
              {assignees?.length > 3 && (
                <>
                  <Avatar
                    label={`+${assignees?.length - 3}`}
                    className={`pointer ${overflowAvatarId} table-assignee-avatar`}
                    style={{ ...OVERFLOW_AVATAR_STYLE, borderRadius: '12px' }}
                    shape="circle"
                  />
                  {remainingAssigneesTooltip && (
                    <Tooltip target={overflowTooltipTarget} position="top" className="case-tooltip mb-3 text-capitalize">
                      {remainingAssigneesTooltip}
                    </Tooltip>
                  )}
                </>
              )}
            </AvatarGroup>
          </div>
        </React.Fragment>
      );
    },
    [OVERFLOW_AVATAR_STYLE]
  );
  // Template for subtasks counter with expandable dropdown (similar to TaskCard)
  const subtasksTemplate = useCallback(
    (rowData) => {
      const subtaskStats = {
        total: rowData?.sub_task?.length || 0,
        completed: rowData?.sub_task?.filter((st) => st?.sub_task_status === true)?.length || 0,
      };

      const isExpanded = expandedSubtasks.has(rowData?.task_id);

      // Toggle subtask expansion
      const toggleSubtasks = () => {
        const newSet = new Set(expandedSubtasks);
        if (newSet.has(rowData?.task_id)) {
          newSet.delete(rowData?.task_id);
        } else {
          newSet.add(rowData?.task_id);
        }
        setExpandedSubtasks(newSet);
        subtaskToggleState.set(rowData?.task_id, !isExpanded);
      };

      return (
        <React.Fragment>
          <span className="p-column-title text-break">Subtasks</span>
          <div className="subtasks-column-wrapper">
            <div className="subtask-count-container d-flex align-items-center pointer" onClick={toggleSubtasks}>
              <span className="subtask-count">
                {subtaskStats?.completed}/{subtaskStats?.total}
              </span>
              {subtaskStats?.total > 0 && (
                <>
                  <span className="ms-2 small black-600">
                    {isExpanded ? <i className="pi pi-angle-up" /> : <i className="pi pi-angle-down" />}
                  </span>
                </>
              )}
            </div>

            {/* Subtasks Dropdown List - similar to TaskCard */}
            {isExpanded && subtaskStats?.total > 0 && (
              <div className="subtasks-dropdown mt-2">
                {rowData?.sub_task?.map((subTask, subIdx) => {
                  return (
                    <div key={subIdx} className="subtask-item d-flex align-items-center p-2">
                      <Checkbox
                        checked={subTask?.sub_task_status}
                        disabled={false}
                        className="pointer me-2"
                        onChange={(e) => handleSubTaskCheckBox(e.checked, subIdx, rowData, stage?.task_stage_id)}
                      />
                      <span className="flex-grow-1 small ellipsis-text" title={subTask?.sub_task_description}>
                        {subTask?.sub_task_description}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </React.Fragment>
      );
    },
    [expandedSubtasks, handleSubTaskCheckBox, stage?.task_stage_id]
  );

  // Template for priority badge with color coding
  const priorityTemplate = useCallback((rowData) => {
    const priority = rowData?.priority?.toUpperCase() || '-';
    let badgeClass = 'badge badge-secondary';

    const priorityClassMap = {
      HIGH: 'rgb(210, 82, 82)',
      MEDIUM: 'rgb(245, 215, 128)',
      LOW: 'rgb(146, 199, 155)',
      'NO PRIORITY': 'rgb(248, 128, 109)',
    };

    if (priority === 'HIGH') {
      badgeClass = 'badge text-white';
    } else if (priority === 'MEDIUM') {
      badgeClass = 'badge text-dark';
    } else if (priority === 'LOW') {
      badgeClass = 'badge text-white';
    } else {
      badgeClass = 'badge text-white';
    }

    return (
      <React.Fragment>
        <span className="p-column-title text-break">Priority</span>
        <span
          className={badgeClass}
          style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem', backgroundColor: priorityClassMap[priority], letterSpacing: '0.05rem' }}
        >
          {priority}
        </span>
      </React.Fragment>
    );
  }, []);

  // Template for due date
  const dueDateTemplate = useCallback(
    (rowData) => {
      if (!rowData?.due_date) return <span>-</span>;
      const timezone = userContext?.userDetails?.timezone;
      const formattedDate = convertDateFromApiToUserTimezone(rowData?.due_date, timezone, 'MMM DD, YYYY');
      return (
        <React.Fragment>
          <span className="p-column-title text-break">Due Date</span>
          <span
            className={`due-date ellipsis-text ${isOverdue(rowData) ? 'text-danger' : ''}`}
            style={{
              display: 'block',
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={handleDateTimeOffset(timezone, rowData?.due_date, constants?.month_date_year_format) || undefined}
          >
            {formattedDate}
          </span>
        </React.Fragment>
      );
    },
    [isOverdue, userContext?.userDetails?.timezone]
  );

  // Template for description preview
  const descriptionTemplate = useCallback((rowData, props) => {
    const desc = typeof rowData?.description === 'string' ? rowData?.description : '';
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Description</span>
        <div
          className={`description-preview ellipsis-text description-${rowData?.task_stage}-${props?.rowIndex}`}
          style={{
            display: 'block',
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          tabIndex={0}
          aria-label={desc ? desc : 'No description'}
        >
          {desc ? desc : '-'}
        </div>
        {desc && <Tooltip position="top" target={`.description-${rowData?.task_stage}-${props?.rowIndex}`} content={desc} />}
      </React.Fragment>
    );
  }, []);

  // Template for recent comment preview
  const recentCommentTemplate = useCallback((rowData) => {
    // Safely handle recent_comment - could be string or object
    let commentText = '';
    if (typeof rowData?.recent_comment === 'string') {
      commentText = rowData?.recent_comment;
    } else if (rowData?.recent_comment && typeof rowData?.recent_comment === 'object') {
      commentText = rowData?.recent_comment?.comment || '';
    }

    return (
      <React.Fragment>
        <span className="p-column-title text-break">Recent Comment</span>
        <div
          className="recent-comment-preview ellipsis-text"
          style={{
            display: 'block',
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={commentText || undefined}
        >
          {commentText ? commentText : '-'}
        </div>
      </React.Fragment>
    );
  }, []);

  // Template for actions (edit, delete, etc.)
  const actionsTemplate = useCallback(
    (rowData) => {
      // Get the task-specific menu ref
      const taskMenuRef = getMenuRef(rowData?.task_id);

      const menuItems = [
        // {
        //   label:
        //     rowData?.task_status === 'completed' || rowData?.task_stage === doneStage?.task_stage_id ? 'Completed' : 'Mark as complete',
        //   command: () => markAsComplete(rowData?.task_id),
        //   disabled: rowData?.task_status === 'completed' || rowData?.task_stage === doneStage?.task_stage_id,
        // },
        {
          label: 'Edit Task',
          command: () => handleEditTask(rowData?.task_id),
        },
        {
          label: 'Update Due Date',
          command: () => handleUpdateDueDate(rowData?.task_id),
        },
        {
          label: 'Change Priority',
          command: () => handleChangePriority(rowData?.task_id),
        },
        {
          label: 'Reassign Task',
          command: () => handleReassignTask(rowData?.task_id),
        },
        {
          label: 'Move to Another Stage',
          items: taskStages
            ?.filter((s) => s?.task_stage_id !== stage?.task_stage_id)
            ?.map((s) => ({
              label: formatStageName(s?.task_stage_name),
              command: () => handleMoveToStage(rowData?.task_id, s?.task_stage_id),
            })),
        },
      ];

      return (
        <React.Fragment>
          <span className="p-column-title text-break">Actions</span>
          <div className="actions-container d-flex gap-1 flex-nowrap align-items-center">
            <Button
              className="p-button p-button-sm p-button-primary rounded-3 d-flex justify-content-center align-items-center mark-complete-btn"
              onClick={() => markAsComplete(rowData?.task_id)}
              disabled={rowData?.task_status === 'completed' || rowData?.task_stage === doneStage?.task_stage_id}
            >
              <span
                style={{
                  lineHeight: '1.1',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  fontSize: '0.9rem',
                }}
                title={
                  rowData?.task_status === 'completed' || rowData?.task_stage === doneStage?.task_stage_id ? 'Completed' : 'Mark Complete'
                }
              >
                {rowData?.task_status === 'completed' || rowData?.task_stage === doneStage?.task_stage_id ? 'Completed' : 'Mark Complete'}
              </span>
            </Button>
            <Button
              icon="pi pi-ellipsis-v"
              className="p-button p-button-text p-button-sm d-flex align-items-center"
              onClick={(e) => taskMenuRef.current?.toggle(e)}
              aria-haspopup="true"
              aria-controls={`menu-${rowData?.task_id}`}
              style={{ flexShrink: 0 }}
            />
            <TieredMenu
              ref={taskMenuRef}
              model={menuItems}
              className="d-flex"
              popup
              id="popup_menu"
              style={{ minWidth: '180px' }} // Prevent forced wrap
            />
          </div>
          <style>
            {`
              @media (max-width: 576px) {
                .mark-complete-btn span {
                  font-size: 0.85em;
                }
                .mark-complete-btn {
                  padding-left: 0.5rem !important;
                  padding-right: 0.5rem !important;
                  min-width: unset !important;
                }
              }
              .actions-container {
                flex-wrap: nowrap !important;
              }
            `}
          </style>
        </React.Fragment>
      );
    },
    [
      markAsComplete,
      doneStage,
      taskStages,
      stage?.task_stage_id,
      handleEditTask,
      handleUpdateDueDate,
      handleChangePriority,
      handleReassignTask,
      handleMoveToStage,
      getMenuRef,
    ]
  );

  // Custom paginator component - similar to useFirmUserCaseReportRDS hook
  const customPaginator = useMemo(
    () => (
      <EllipsisPagination
        currentPage={currentPage}
        totalRecords={totalCount || 0}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showPageSizeDropdown={true}
        isAgendaview={true}
        pageSizeOptions={paginationDropdownOptions}
      />
    ),
    [currentPage, totalCount, pageSize, handlePageChange, handlePageSizeChange]
  );

  // Define header column group - must be before conditional render
  const headerColumnGroup = useMemo(() => {
    const isDefaultColumn = stage?.is_default;
    const isLocked = stage?.isLocked;

    const stageMenuItems = [
      {
        label: 'Manage Column',
        icon: 'pi pi-cog',
        command: handleManageColumn,
        disabled: false,
      },
      {
        label: 'Rename',
        icon: 'pi pi-pencil',
        command: () => {
          setShowRenameStageDialog(true);
        },
        disabled: isDefaultColumn || isLocked,
      },
      {
        label: 'Duplicate',
        icon: 'pi pi-copy',
        command: handleDuplicateStage,
        disabled: false,
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        command: handleDeleteStage,
        disabled: isDefaultColumn || isLocked,
      },
    ];

    const totalColumns = 9;

    return (
      <ColumnGroup className="p-datatable-responsive">
        <Row className="p-datatable-responsive">
          <Column
            className="p-datatable-responsive"
            header={
              <div className="d-flex justify-content-between align-items-center w-100">
                <span>{formatStageName(stage?.task_stage_name)}</span>
              </div>
            }
            colSpan={totalColumns - 2}
            style={{ fontWeight: 'bold', fontSize: '1rem' }}
          />
          <Column
            className="p-datatable-responsive"
            style={{ textAlign: 'right', paddingRight: '1rem', fontWeight: '600' }}
            header={
              <div className="d-flex justify-content-end align-items-center gap-2">
                <span className="column-badge">{`${totalCount || 0} tasks`}</span>
                <Button
                  icon="pi pi-plus"
                  className="p-button p-button-text p-button-sm"
                  onClick={() => openAddTask(stage?.task_stage_id)}
                  title="Add new task to this stage"
                  style={{ fontSize: '0.875rem' }}
                />
                <Button
                  icon="pi pi-ellipsis-v"
                  className="p-button p-button-text p-button-sm"
                  onClick={(e) => stageMenuRef?.current?.toggle(e)}
                  aria-haspopup="true"
                  aria-controls="stage-menu"
                  style={{ fontSize: '0.875rem' }}
                />
                <TieredMenu ref={stageMenuRef} model={stageMenuItems} popup id="popup_menu" />
              </div>
            }
            colSpan={2}
          />
        </Row>
        <Row>
          <Column field="task_title" header={`${isFromCase || isFromLead ? 'Title' : 'Title & Case/Lead'}`} />
          <Column field="task_status" header="Status" />
          <Column field="assignee_name" header="Assignees" />
          <Column field="total_subtasks" header="Subtasks" />
          <Column field="priority" header="Priority" />
          <Column field="due_date" header="Due Date" />
          <Column field="description" header="Description" />
          <Column field="recent_comment" header="Recent Comment" />
          <Column field="actions" header="Actions" />
        </Row>
      </ColumnGroup>
    );
  }, [
    stage?.task_stage_name,
    stage?.task_stage_id,
    stage?.is_default,
    stage?.isLocked,
    totalCount,
    handleDuplicateStage,
    handleDeleteStage,
    handleManageColumn,
    openAddTask,
  ]);

  const handleGetClientUserList = () => {
    const condition = Boolean(selectedTask?.case_id);

    getClientUserList({
      caseId: selectedTask?.case_id,
      is_mandatory_lawft_access: false,
      is_team_member_list: condition,
      all_contacts: condition,
      is_active: true,
      is_include_groups: true,
      // active_groups: true,
      excludeFromLoading: true,
    })
      .then((response) => {
        const rawUserList = response?.data || [];

        // Apply the same filtering logic as ViewTaskRefactored.jsx
        const filteredUserList = rawUserList.filter((user) => {
          const contactUserList = user?.access_level === 'client';

          const isActiveUser = user?.is_confirmed && user?.status === 'ACTIVE';
          const isArchivedConfirmed = user?.res_type !== 'user-group' && (user?.status === 'ARCHIVED' || user?.is_confirmed === false);

          const isIndividualUser = selectedTask?.assign_to?.some(
            (firmUser) => firmUser?.cognito_username === user?.client_cognito_username
          );

          const isActiveGroup = user?.res_type === 'user-group' && user?.status === 'ACTIVE';
          const isArchiveGroupList =
            user?.res_type === 'user-group' &&
            user?.status === 'ARCHIVED' &&
            selectedTask?.groups?.some((group) => group?.group_id === user?.user_group_id);

          return contactUserList || isActiveUser || (isArchivedConfirmed && isIndividualUser) || isActiveGroup || isArchiveGroupList;
        });
        // Format the filtered list

        const formattedList = formatTaskContactList(filteredUserList);
        const sortedList = formattedList?.map((item) => {
          if (item?.code === 'Firm Users') {
            if (!selectedTask?.case_id) {
              return {
                ...item,
                items: item?.items
                  .filter((user) => user?.firm_user_id)
                  .sort((a, b) => {
                    const nameA = (a?.first_name || a?.assignee_name || '').toLowerCase();
                    const nameB = (b?.first_name || b?.assignee_name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  }),
              };
            } else {
              return {
                ...item,
                items: item?.items?.sort((a, b) => {
                  const nameA = (a?.first_name || a?.assignee_name || '').toLowerCase();
                  const nameB = (b?.first_name || b?.assignee_name || '').toLowerCase();
                  return nameA.localeCompare(nameB);
                }),
              };
            }
          } else if (item?.code === 'Groups') {
            return {
              ...item,
              items: item?.items?.sort((a, b) => (a?.group_name?.toLowerCase() || '').localeCompare(b?.group_name?.toLowerCase() || '')),
            };
          }
          return item;
        });

        setClientData(sortedList);
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    handleGetClientUserList();
  }, [showReassignDialog]);

  return (
    <>
      <div
        className="datatable-responsive overflow-auto my-5"
        style={{
          width: '100%',
          maxWidth: '100%',
          // Prevent page-level horizontal scroll on mobile.
          // PrimeReact's responsive mode already stacks cells on small screens, so X-scroll is not needed.
          overflowX: isMobileView ? 'hidden' : 'auto',
        }}
      >
        <ResizableDataTable
          headerColumnGroup={headerColumnGroup}
          value={displayItems}
          storageKey={`tasks_table_${stage?.task_stage_id}`}
          className="p-datatable-responsive w-100"
          emptyMessage={isLoading && displayItems?.length === 0 ? 'Loading tasks...' : `No tasks found`}
          scrollable
          responsiveLayout="scroll"
          stripedRows
          // Desktop: fixed layout enables true truncation/ellipsis within resizable columns.
          // Mobile: keep auto so table can overflow and horizontal scrolling stays responsive.
          tableStyle={{ width: '100%', minWidth: '100%', tableLayout: isMobileView ? 'auto' : 'fixed' }}
          columns={[
            { field: 'task_title', header: 'Title & Case/Lead', body: titleTemplate, bodyClassName: 'ellipsis-text capitalize-text' },
            { field: 'task_status', header: 'Status', body: statusTemplate, bodyClassName: 'ellipsis-text' },
            { field: 'assignee_name', header: 'Assignees', body: assigneesTemplate, bodyClassName: 'ellipsis-text' },
            { field: 'total_subtasks', header: 'Subtasks', body: subtasksTemplate, bodyClassName: 'ellipsis-text' },
            { field: 'priority', header: 'Priority', body: priorityTemplate, bodyClassName: 'ellipsis-text' },
            { field: 'due_date', header: 'Due Date', body: dueDateTemplate, bodyClassName: 'ellipsis-text' },
            { field: 'description', header: 'Description', body: descriptionTemplate, bodyClassName: 'ellipsis-text' },
            { field: 'recent_comment', header: 'Recent Comment', body: recentCommentTemplate, bodyClassName: 'ellipsis-text' },
            { field: 'actions', header: 'Actions', body: actionsTemplate, bodyClassName: 'ellipsis-text' },
          ]}
        />

        {/* Pagination component - only show if more than 5 items total */}
        {totalCount > pageSize && customPaginator}
      </div>

      {/* Update Due Date Dialog */}
      <Dialog
        visible={showDueDateDialog}
        onHide={() => {
          setShowDueDateDialog(false);
          setSelectedTask(null);
          setEditDueDate(null);
          reset({ dueDate: null });
          clearErrors('dueDate');
        }}
        header={`Update Due Date - ${selectedTask?.task_title || selectedTask?.task_name || ''}`}
        modal
        style={{ width: '35vw' }}
      >
        <div className="p-field mb-3">
          <label htmlFor="dueDate" className="mb-2 d-block">
            Due Date <span className="alert-star">*</span>
          </label>
          <Controller
            control={control}
            name="dueDate"
            rules={{
              required: 'Due date is required',
              validate: (value) => {
                if (!value) {
                  return 'Due date is required';
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Calendar
                  className={`filter-calender input-shadow w-100 ${fieldState?.error ? 'p-invalid' : ''}`}
                  dateFormat="mm/dd/yy"
                  showIcon
                  monthNavigator
                  yearNavigator
                  yearRange={`${new Date().getFullYear() - 100}:${new Date().getFullYear() + 50}`}
                  id="dueDate"
                  value={field?.value || editDueDate}
                  onChange={(e) => {
                    field.onChange(e?.value);
                    setEditDueDate(e?.value);
                  }}
                  onBlur={field?.onBlur}
                />
                {fieldState?.error && <InputErrorMessage>{fieldState?.error?.message || 'Due date is required'}</InputErrorMessage>}
              </>
            )}
          />
        </div>
        <div className="d-flex gap-2 justify-content-end">
          <Button
            label="Cancel"
            onClick={() => {
              setShowDueDateDialog(false);
              setSelectedTask(null);
              setEditDueDate(null);
              reset({ dueDate: null });
              clearErrors('dueDate');
            }}
            className="p-button-secondary"
          />
          <Button label="Save" className="p-button-secondary me-2" onClick={handleSaveDueDate} />
        </div>
      </Dialog>

      {/* Change Priority Dialog */}
      <Dialog
        visible={showPriorityDialog}
        onHide={() => {
          setShowPriorityDialog(false);
          setSelectedTask(null);
          setEditPriority('');
          reset({ priority: '' });
          clearErrors('priority');
        }}
        header={`Change Priority - ${selectedTask?.task_title || selectedTask?.task_name || ''}`}
        modal
        style={{ width: '35vw' }}
      >
        <div className="p-field mb-3">
          <label htmlFor="priority" className="mb-2 d-block">
            Priority <span className="alert-star">*</span>
          </label>
          <Controller
            control={control}
            name="priority"
            rules={{
              required: 'Priority is required',
              validate: (value) => {
                if (!value || value?.trim() === '') {
                  return 'Priority is required';
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <PriorityDropdown
                  id="priority"
                  value={field?.value || editPriority}
                  onChange={(e) => {
                    field.onChange(e?.value);
                    setEditPriority(e?.value);
                  }}
                  onBlur={field?.onBlur}
                  placeholder="Select Priority"
                  className={`w-100 ${fieldState?.error ? 'p-invalid' : ''}`}
                  filter
                />
                {fieldState?.error && <InputErrorMessage>{fieldState?.error?.message || 'Priority is required'}</InputErrorMessage>}
              </>
            )}
          />
        </div>
        <div className="d-flex gap-2 justify-content-end">
          <Button
            label="Cancel"
            onClick={() => {
              setShowPriorityDialog(false);
              setSelectedTask(null);
              setEditPriority('');
              reset({ priority: '' });
              clearErrors('priority');
            }}
            className="p-button-secondary"
          />
          <Button label="Save" className="p-button-secondary me-2" onClick={handleSavePriority} />
        </div>
      </Dialog>

      {/* Reassign Task Dialog */}
      <Dialog
        visible={showReassignDialog}
        onHide={() => {
          setShowReassignDialog(false);
          setSelectedTask(null);
          setAssigneeData([]);
          setSelectedGroups([]);
          clearErrors('assignees');
          reset({ assignees: [] });
        }}
        header={`Reassign Task - ${selectedTask?.task_title || selectedTask?.task_name || ''}`}
        modal
        style={{ width: '35vw' }}
        breakpoints={{ '960px': '75vw', '640px': '90vw' }}
      >
        <div className="p-field mb-3">
          <label htmlFor="assignees" className="mb-2 d-block">
            Select Assignees <span className="alert-star">*</span>
          </label>
          <Controller
            control={control}
            name="assignees"
            rules={{
              required: 'Assignees are required',
              validate: (value) => {
                if (!value || (Array?.isArray(value) && value?.length === 0)) {
                  return 'Assignees are required';
                }
                return true;
              },
            }}
            render={({ field, fieldState }) => {
              // Filter out invalid IDs from the current value to prevent empty chips
              const filteredValue = filterValidAssigneeIds(field?.value || []);

              return (
                <>
                  <MultiSelect
                    id="assignees"
                    value={filteredValue}
                    onChange={(e) => {
                      const selectedValue = e?.value || [];
                      // Filter out any invalid IDs immediately
                      const validSelectedValue = filterValidAssigneeIds(selectedValue);

                      field.onChange(validSelectedValue);

                      // Find selected assignee objects based on valid selected values
                      const selectedAssignees = [];
                      const selectedGroups = [];
                      if (Array?.isArray(validSelectedValue) && Array?.isArray(clientData)) {
                        clientData?.forEach((item) => {
                          item?.items?.forEach((assignee) => {
                            if (assignee?.res_type === 'user-group') {
                              const optionId = assignee?.cognito_username || assignee?.assignee_id || assignee?.group_id;
                              if (validSelectedValue?.includes(optionId)) {
                                selectedGroups.push(assignee);
                              }
                            } else {
                              // Check for cognito_username or assignee_id to handle cases where only cognito_username exists
                              // Match the order used in optionValue: cognito_username first, then assignee_id
                              const optionId = assignee?.cognito_username || assignee?.assignee_id || assignee?.group_id;
                              if (validSelectedValue?.includes(optionId)) {
                                selectedAssignees.push(assignee);
                              }
                            }
                          });
                        });
                      }

                      setAssigneeData(selectedAssignees);
                      setSelectedGroups(selectedGroups);
                    }}
                    onBlur={field.onBlur}
                    options={clientData}
                    itemTemplate={assignToItemTemplate}
                    optionLabel="assignee_name"
                    optionValue={(v) => v?.cognito_username || v?.assignee_id || v?.group_id}
                    optionGroupLabel="assignee_name"
                    optionGroupChildren="items"
                    placeholder="Select Assignee"
                    maxSelectedLabels={3}
                    className={`w-100 input-shadow F-size14 input-height ${fieldState?.error ? 'p-invalid' : ''}`}
                    display="chip"
                    filter
                    filterPlaceholder="Search"
                    resetFilterOnHide
                    showSelectAll={false}
                    required
                  />
                  {fieldState?.error && <InputErrorMessage>{fieldState?.error?.message || 'Assignees are required'}</InputErrorMessage>}
                </>
              );
            }}
          />
        </div>
        <div className="d-flex gap-2 justify-content-end">
          <Button
            label="Cancel"
            onClick={() => {
              setShowReassignDialog(false);
              setSelectedTask(null);
              setAssigneeData([]);
              setSelectedGroups([]);
              clearErrors('assignees');
              reset({ assignees: [] });
            }}
            className="p-button-secondary"
          />
          <Button label="Save" className="p-button-secondary me-2" onClick={handleSaveReassign} />
        </div>
      </Dialog>

      {/* Move to Another Stage Dialog */}
      <Dialog
        visible={showMoveDialog}
        onHide={() => {
          setShowMoveDialog(false);
          setSelectedTask(null);
          setTargetStage('');
        }}
        header={`Move Task - ${selectedTask?.task_title || selectedTask?.task_name || ''}`}
        modal
        style={{ width: '50vw' }}
      >
        <div className="p-field mb-3">
          <label htmlFor="targetStage" className="mb-2 d-block">
            Select Stage
          </label>
          <Dropdown
            id="targetStage"
            value={targetStage}
            options={taskStages
              ?.filter((s) => s?.task_stage_id !== stage?.task_stage_id)
              ?.map((s) => ({ label: formatStageName(s?.task_stage_name), value: s?.task_stage_id }))}
            onChange={(e) => setTargetStage(e?.value)}
            placeholder="Select Stage"
            className="w-100"
          />
        </div>
        <div className="d-flex gap-2 justify-content-end">
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={() => {
              setShowMoveDialog(false);
              setSelectedTask(null);
              setTargetStage('');
            }}
            className="p-button-secondary"
          />
          <Button label="Move" icon="pi pi-check" onClick={handleSaveMove} />
        </div>
      </Dialog>

      {/* Rename Stage Dialog - Using same dialog from Kanban View */}
      {showRenameStageDialog && (
        <RenameColumnDialog
          visible={showRenameStageDialog}
          onHide={() => setShowRenameStageDialog(false)}
          onSubmit={confirmRenameStage}
          currentName={stage?.task_stage_name}
        />
      )}

      {/* Manage Column Dialog */}
      {showManageColumn && <ManageColumn column={stage} onHide={() => setShowManageColumn(false)} />}
    </>
  );
};

export default TableContent;
